import React, { useState, useEffect, useRef, useCallback } from 'react';

const CameraComponent = ({ onCapture, onClose }) => {
    const [devices, setDevices] = useState([]);
    const [activeDeviceId, setActiveDeviceId] = useState(undefined);
    const [error, setError] = useState('');
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    const startCamera = useCallback(async (deviceId) => {
        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            const constraints = {
                video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' }
            };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            streamRef.current = stream;

            // If no device ID was specified (first run), try to get the active one from the stream
            if (!deviceId) {
                const track = stream.getVideoTracks()[0];
                const settings = track.getSettings();
                if (settings.deviceId) {
                    setActiveDeviceId(settings.deviceId);
                }
            }

            // Re-enumerate devices to ensure we have the full list after permission is granted
            const allDevices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = allDevices.filter(device => device.kind === 'videoinput');
            setDevices(videoDevices);
            console.log("Devices found:", videoDevices);

        } catch (err) {
            console.error("Error accessing camera:", err);
            setError("Could not access camera. Please ensure you have granted permission.");
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    }, []);

    const switchCamera = useCallback(() => {
        if (devices.length < 2) return;

        const currentIndex = devices.findIndex(d => d.deviceId === activeDeviceId);
        const nextIndex = (currentIndex + 1) % devices.length;
        const nextDeviceId = devices[nextIndex].deviceId;

        setActiveDeviceId(nextDeviceId);
        startCamera(nextDeviceId);
    }, [devices, activeDeviceId, startCamera]);

    useEffect(() => {
        const getDevices = async () => {
            try {
                const allDevices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = allDevices.filter(device => device.kind === 'videoinput');
                setDevices(videoDevices);
            } catch (err) {
                console.error("Error enumerating devices:", err);
            }
        };
        getDevices();
    }, []);

    useEffect(() => {
        startCamera(activeDeviceId);
        return () => {
            stopCamera();
        };
    }, [startCamera, stopCamera, activeDeviceId]);

    const capture = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            onCapture(blob);
        }, 'image/jpeg');
    }, [onCapture]);

    return (
        <div>
            <div className="camera-container">
                <video ref={videoRef} autoPlay playsInline className="camera-video"></video>
                <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                {error && <p className="error-message">{error}</p>}
            </div>
            <div className="modal-footer">
                {devices.length > 1 && (
                    <button title="Switch Camera" onClick={switchCamera}>Switch Camera</button>
                )}
                <button title="Scan" onClick={capture}>Scan</button>
                <button title="Cancel scan" onClick={onClose} className="cancel-button">Cancel</button>
            </div>
        </div>
    );
};

export default CameraComponent;
